import { useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import Cached from '@mui/icons-material/Cached'
import KeyIcon from '@mui/icons-material/Key'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'

import type { SortOrder } from 'common/api/v1/types'
import { Appliance, ListApplianceSortableField, Role } from 'common-billing-server/types'
import type { Query } from 'common/query'

import { Banner } from 'common-ui/components/Banner'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDate, formatDateTime } from '../../format-date'
import { makeApplianceDetailsPath, makeEditCustomerPath } from '../../routes/path'
import { Column, DataFetchingPaginatedTable, RefreshableDataFetchingPaginatedTable } from '../common/Table'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import type { PaginatedDataFetchingFilter } from '../common/hooks/hook-paginated-data-fetching'
import { RoleBasedComponent, RoleBasedLink } from '../common/RoleBasedLink'
import { RotatingButton } from '../common/RotatingButton'
import Wrapper from '../common/Wrapper'
import { SshAccessDialog } from './SshAccessDialog'

interface ApplianceRow extends Appliance {
    id: string
}

export const ListAppliancesPage = () => {
    const billingApi = useBillingApi()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const customerId = searchParams.get('customerId') ?? undefined
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [isSshAccessDialogOpen, setIsSshAccessDialogOpen] = useState(false)
    const [notice, setNotice] = useState<string>()

    const columns: Column<ApplianceRow, ListApplianceSortableField>[] = useMemo(
        () => [
            {
                title: 'Serial number',
                valueForColumn: (row) => row.serialNumber,
                sortField: ListApplianceSortableField.serialNumber,
            },
            {
                title: 'SKU',
                valueForColumn: (row) => row.productSku ?? '—',
            },
            {
                title: 'Purchase date (UTC)',
                valueForColumn: (row) => (row.purchasedAt ? formatDate(row.purchasedAt) : '—'),
            },
            {
                title: 'Customer',
                isClickable: false,
                valueForColumn: (row) => (
                    <RoleBasedLink minimumAllowedRole={Role.admin} to={makeEditCustomerPath(row.customer.id)}>
                        {row.customer.name}
                    </RoleBasedLink>
                ),
                sortField: ListApplianceSortableField.customerName,
            },
        ],
        []
    )

    async function fetchAppliances({
        filter,
        order,
        ...query
    }: Query<PaginatedDataFetchingFilter<Record<string, never>>, SortOrder<ListApplianceSortableField>>) {
        try {
            const result = await billingApi.listAppliances({
                ...query,
                filter: {
                    customerId,
                    searchName: filter?.searchString,
                },
                order,
            })
            return { ...result, items: result.items.map((item) => ({ ...item, id: item.serialNumber })) }
        } catch (error) {
            addFailedOperation({
                id: 'list-appliances',
                message: 'Failed fetching appliances',
                error,
                retryFn: tableRef.current?.refreshData,
            })
            throw error
        }
    }

    const openSshAccessDialog = () => {
        removeFailedOperation('load-ssh-key')
        removeFailedOperation('ssh-certificate')
        setIsSshAccessDialogOpen(true)
    }

    return (
        <Wrapper
            actions={
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <RoleBasedComponent minimumAllowedRole={Role.admin}>
                        <Button variant="outlined" startIcon={<KeyIcon />} onClick={openSshAccessDialog}>
                            Get SSH access
                        </Button>
                    </RoleBasedComponent>
                    <RotatingButton
                        key="refresh"
                        tooltip="Refresh"
                        onClick={() => tableRef.current?.refreshData()}
                        Component={<Cached />}
                    />
                </Stack>
            }
        >
            <Paper>
                <DataFetchingPaginatedTable
                    key={customerId}
                    myRef={tableRef}
                    api={fetchAppliances}
                    columns={columns}
                    emptyRowsMessage="No appliances found"
                    onRowClicked={(appliance) => navigate(makeApplianceDetailsPath(appliance.serialNumber))}
                    searchParameters={{ searchBarPlaceholder: 'Search by serial number, SKU, or customer...' }}
                />
            </Paper>
            {errorBanner}
            {notice && <Banner id="notice" isOpen={true} message={notice} onClose={() => setNotice(undefined)} />}
            <SshAccessDialog
                open={isSshAccessDialogOpen}
                onClose={() => setIsSshAccessDialogOpen(false)}
                onIssued={(certificate) =>
                    setNotice(
                        `Downloaded an SSH certificate for ${certificate.applianceSerial} valid until ${formatDateTime(certificate.validUntilDate)}.`
                    )
                }
                onFailed={addFailedOperation}
            />
        </Wrapper>
    )
}
